/* eslint-disable @typescript-eslint/no-explicit-any */
import { ButtonVariants } from '@dcxwam/dcx-wam-ui-foundations'
import {
  clearCart,
  clearCartViaCbxOrders,
  createCart
} from '../../../../services/product/cart-flow'
import {
  CartOperationError,
  ORDER_TYPE_CONFIRMATION,
  buildTokenCredentials,
  initializeContext,
  validateAndPrepareCart,
  validateLoyaltyTier,
  validateOrderType,
  validateQuantity,
  addProductToCart,
  addProductToCartViaCbx,
  handleSuccess,
  showError,
  setButtonState,
  shouldUseCbxCart,
  shouldRouteToCbx
} from './cart-operations'
import { logApi, ConsoleLevels } from '../../../../services/api-logging/log-api'

// Re-export types for backward compatibility
export type { HandleAddToCartProps } from './types'

/**
 * Handle wrong order type by clearing cart and retrying.
 * Routes to CBX Orders API when CBX_ORDERS_CART toggle is enabled.
 */
export const handleWrongOrderType: (props: {
  username: string
  token: string
  guid: string
  apiUrl: string
  setDialogErrorMessageIsOpen: (arg: boolean) => void
  addToCartProps: import('./types').HandleAddToCartProps
  baseSiteId: string
  language: string
}) => Promise<void> = async ({ ...props }) => {
  const {
    username,
    token,
    guid,
    apiUrl,
    setDialogErrorMessageIsOpen,
    addToCartProps,
    baseSiteId,
    language
  } = props

  try {
    const useCbxCart = shouldUseCbxCart(addToCartProps.cbxOrdersConfig)

    if (useCbxCart) {
      // CBX mode: delete via CBX only. The cart is recreated implicitly by the
      // subsequent CBX add-to-cart, so the Hybris createCart call is skipped —
      // a Hybris request during a CBX flow is not allowed.
      const consumerId = username === 'anonymous' ? 'anonymous' : username
      const cbxToken = addToCartProps.cookies?.gig_jwt || ''
      const cartId = guid || ''

      await clearCartViaCbxOrders({
        baseURL: addToCartProps.cbxOrdersConfig!.baseURL!,
        brand: addToCartProps.cbxOrdersConfig!.brand || '',
        channel: addToCartProps.cbxOrdersConfig!.channel || '',
        consumerId,
        cartId,
        countryCode: addToCartProps.cbxOrdersConfig!.countryCode || '',
        targetGroup: addToCartProps.cbxOrdersConfig!.targetGroup || 'b2c',
        token: cbxToken
      })
    } else {
      await clearCart({ username, token, guid, apiUrl, baseSiteId })
      await createCart({ username, token, apiUrl, language, baseSiteId })
    }

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    await handleAddToCart({ ...addToCartProps })
    setDialogErrorMessageIsOpen(false)
  } catch (e) {
    logApi({
      text: `Unable to clear/recreate cart on order-type change: ${
        e instanceof Error ? e.message : 'unknown error'
      }`,
      status: '',
      level: ConsoleLevels.Error
    })
  }
}

/**
 * Main entry point for add to cart functionality.
 * Supports two backend paths:
 * - CBX Orders (useCbxOrders: true): Uses @fe-sdk/cbx-orders SDK
 * - Legacy Hybris (useCbxOrders: false): Uses direct Hybris REST API
 */
export const handleAddToCart = async (
  props: import('./types').HandleAddToCartProps
) => {
  const { defaultErrorMessage } = props

  try {
    setButtonState(props, true, true)

    // Routing is the OR of two independent reasons (see shouldRouteToCbx):
    //  1. props.useCbxOrders — order types already migrated to CBX
    //     (AD/ZAS/ZKL/ZTA/ZBC) always route to CBX, regardless of the toggle.
    //  2. the CBX_ORDERS_CART toggle (+ baseURL) — governs endpoints still under
    //     QA test; ON routes to CBX, OFF reverts to Hybris for comparison. This
    //     toggle is TEMPORARY and removed after the QA cutover.
    const shouldAttemptCbxOrders = shouldRouteToCbx(props)
    const tokenCreds = buildTokenCredentials(props)
    const context = await initializeContext(props, tokenCreds)

    if (shouldAttemptCbxOrders) {
      // ── CBX Orders path ──
      await validateLoyaltyTier(props)

      // Validate order type for CBX path
      const cartOrderType = context.cartData?.cart?.orderType?.typeId
      const isOrderTypeCorrect =
        !cartOrderType || props.orderType === cartOrderType
      const hasItems = (context.cartData?.cart?.totalItems || 0) > 0

      if (!isOrderTypeCorrect && hasItems) {
        throw new CartOperationError(ORDER_TYPE_CONFIRMATION, {
          title: props.errorContent?.differentOrderTypeTitle,
          message: props.errorContent?.differentOrderTypeMessage,
          actions: [
            {
              label: props.errorContent?.differentOrderTypeConfirmCTALabel,
              isCbx: true
            },
            {
              label: props.errorContent?.addToCartDialogCancelCTALabel,
              buttonVariant: ButtonVariants.Outlined,
              isCancel: true
            }
          ]
        })
      }

      const responseJson = await addProductToCartViaCbx({ props, context })
      handleSuccess(props, responseJson)
    } else {
      // ── Legacy Hybris path ──
      const cartResult = await validateAndPrepareCart(context, tokenCreds, {
        cbxOrdersConfig: props.cbxOrdersConfig,
        cookies: props.cookies
      })
      context.guid = cartResult.guid
      context.token = cartResult.token

      const loyaltyTier = await validateLoyaltyTier(props)

      const orderTypeResult = await validateOrderType(
        props,
        context,
        tokenCreds
      )
      if (orderTypeResult) {
        context.guid = orderTypeResult.guid
        context.token = orderTypeResult.token
      }

      validateQuantity(props, context)

      const responseJson = await addProductToCart({
        props,
        context,
        tokenCreds,
        loyaltyTier
      })

      handleSuccess(props, responseJson)
    }
  } catch (error: any) {
    if (error instanceof CartOperationError) {
      if (error.type === ORDER_TYPE_CONFIRMATION) {
        const confirmAction = (error.content.actions as any[])?.find(
          (action: any) => !action.isCancel
        )
        const cancelAction = (error.content.actions as any[])?.find(
          (action: any) => action.isCancel
        )
        const isCbxConfirm = Boolean(confirmAction?.isCbx)

        showError(props, {
          ...error.content,
          actions: [
            {
              ...confirmAction,
              onClick: async () => {
                try {
                  if (isCbxConfirm) {
                    // CBX retry: delete cart on server then create fresh
                    const retryContext = await initializeContext(
                      props,
                      buildTokenCredentials(props)
                    )

                    const cbxBaseURL = props.cbxOrdersConfig?.baseURL
                    if (shouldRouteToCbx(props) && cbxBaseURL) {
                      const cbxConsumerId = retryContext.username || 'anonymous'
                      const cbxToken = props.cookies?.gig_jwt || ''
                      const cbxCartId =
                        retryContext.cartData?.cart?.code ||
                        retryContext.cartData?.cart?.guid ||
                        ''

                      if (cbxCartId) {
                        await clearCartViaCbxOrders({
                          baseURL: cbxBaseURL,
                          brand: props.cbxOrdersConfig?.brand || '',
                          channel: props.cbxOrdersConfig?.channel || '',
                          consumerId: cbxConsumerId,
                          cartId: cbxCartId,
                          countryCode: props.cbxOrdersConfig?.countryCode || '',
                          targetGroup:
                            props.cbxOrdersConfig?.targetGroup || 'b2c',
                          token: cbxToken
                        })
                      }
                    }

                    retryContext.cartData = null

                    const responseJson = await addProductToCartViaCbx({
                      props,
                      context: retryContext
                    })

                    handleSuccess(props, responseJson)
                    props.setDialogErrorMessageIsOpen(false)
                  } else {
                    // Hybris retry: clear and recreate cart
                    const result = await confirmAction.onClick()
                    if (result) {
                      const retryContext = await initializeContext(
                        props,
                        buildTokenCredentials(props)
                      )
                      retryContext.guid = result.guid
                      retryContext.token = result.token

                      const retryLoyaltyTier = await validateLoyaltyTier(props)
                      validateQuantity(props, retryContext)

                      const responseJson = await addProductToCart({
                        props,
                        context: retryContext,
                        tokenCreds: buildTokenCredentials(props),
                        loyaltyTier: retryLoyaltyTier
                      })

                      handleSuccess(props, responseJson)
                      props.setDialogErrorMessageIsOpen(false)
                    }
                  }
                } catch (retryError) {
                  showError(props, {
                    message: null,
                    title: defaultErrorMessage,
                    actions: null
                  })
                }
              }
            },
            {
              ...cancelAction,
              onClick: () => props.setDialogErrorMessageIsOpen(false)
            }
          ]
        })
      } else {
        showError(props, error.content)
      }
    } else if (error?.message && !error.message.includes('User cancelled')) {
      showError(props, {
        message: null,
        title: defaultErrorMessage,
        actions: null
      })
    }
  } finally {
    setButtonState(props, false, false)
  }
}
